import React from 'react'
import { type LucideIcon } from 'lucide-react'
import { cn } from '../../lib/utils'
import { DialogTitle, DialogDescription } from './Dialog'
import { AlertDialogTitle, AlertDialogDescription } from './AlertDialog'

type IconColor = 'teal' | 'amber' | 'rose' | 'slate' | 'violet' | 'emerald'

type Variant = 'dialog' | 'alertDialog'

interface Props {
  icon: LucideIcon
  iconColor?: IconColor
  title: string
  description?: string
  variant?: Variant
}

const COLOR_MAP: Record<IconColor, { wrapper: string; icon: string }> = {
  teal: {
    wrapper: 'bg-teal-100 dark:bg-teal-900/30',
    icon: 'text-teal-600 dark:text-teal-400',
  },
  amber: {
    wrapper: 'bg-amber-100 dark:bg-amber-900/30',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  rose: {
    wrapper: 'bg-rose-100 dark:bg-rose-900/30',
    icon: 'text-rose-600 dark:text-rose-400',
  },
  slate: {
    wrapper: 'bg-slate-100 dark:bg-slate-800',
    icon: 'text-slate-600 dark:text-slate-400',
  },
  violet: {
    wrapper: 'bg-violet-100 dark:bg-violet-900/30',
    icon: 'text-violet-600 dark:text-violet-400',
  },
  emerald: {
    wrapper: 'bg-emerald-100 dark:bg-emerald-900/30',
    icon: 'text-emerald-600 dark:text-emerald-400',
  },
}

export const DialogHeaderWithIcon: React.FC<Props> = ({
  icon: Icon,
  iconColor = 'teal',
  title,
  description,
  variant = 'dialog',
}) => {
  const colors = COLOR_MAP[iconColor]
  const TitleEl = variant === 'alertDialog' ? AlertDialogTitle : DialogTitle
  const DescEl = variant === 'alertDialog' ? AlertDialogDescription : DialogDescription
  return (
    <div className="flex items-start gap-3">
      <div
        className={cn('flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg p-1.5', colors.wrapper)}
        aria-hidden="true"
      >
        <Icon className={cn('h-5 w-5', colors.icon)} />
      </div>
      <div className="flex-1 min-w-0">
        <TitleEl asChild>
          <h2 className="text-base font-semibold text-foreground leading-tight">{title}</h2>
        </TitleEl>
        {description && (
          <DescEl asChild>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5 leading-snug">
              {description}
            </p>
          </DescEl>
        )}
      </div>
    </div>
  )
}
